import React, {useState, useEffect, useMemo, useRef } from 'react';
import dbService from '../../../services/dbService';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import { Form, Button, Row, Col, Card, Spinner } from 'react-bootstrap';
import { CalendarEvent, FiletypeCsv, FiletypePdf } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import DatePicker from 'react-datepicker'; // <-- IMPORT THE NEW COMPONENT
import 'react-datepicker/dist/react-datepicker.css'; // Ensure CSS is imported
import PageContainer from '../../layout/PageContainer';
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// Define a valid, empty initial state for chart data
const initialChartData = {
    labels: [],
    datasets: [{
        label: 'No data',
        data: [],
        backgroundColor: ['#cccccc'],
    }],
};

// Main Component
const ReportDashboard = () => {
    // --- STATE MANAGEMENT ---
    const [allCases, setAllCases] = useState([]);
    const [allAlerts, setAllAlerts] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filters, setFilters] = useState({
        startDate: '', endDate: '', status: 'All', risk: 'All', assignedTo: 'All'
    });
    
    // NEW: Chart data is now in state with a valid initial structure
    const [statusPieData, setStatusPieData] = useState(initialChartData);
    const [riskBarData, setRiskBarData] = useState(initialChartData);
    const [customerTypeData, setCustomerTypeData] = useState(initialChartData);
    const [reportFormat, setReportFormat] = useState('CSV');

    const dashboardRef = useRef(null);

    // Date picker state now uses Date objects
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    
    // --- DATA FETCHING ---
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            const [cases, alerts, users] = await Promise.all([
                dbService.getAll('cases'),
                dbService.getAll('alerts'),
                dbService.getAll('users'),
            ]);
            setAllCases(cases);
            setAllAlerts(alerts);
            setAllUsers(users);
            setIsLoading(false);
        };
        fetchData();
    }, []);

    // --- AD-HOC FILTERING & KPI LOGIC ---
    const filteredData = useMemo(() => {
        return allCases
            .filter(c => !filters.startDate || c.openDate >= filters.startDate)
            .filter(c => !filters.endDate || c.openDate <= filters.endDate)
            .filter(c => filters.status === 'All' || c.status === filters.status)
            .filter(c => filters.risk === 'All' || c.risk === filters.risk)
            .filter(c => filters.assignedTo === 'All' || c.assignedTo === filters.assignedTo);
    }, [allCases, filters]);

    const kpis = useMemo(() => {
        const totalAlertsReviewed = allAlerts.filter(a => a.status !== 'New').length;
        const casesCreatedFromAlerts = allAlerts.filter(a => a.status === 'Case Created').length;
        const totalCases = filteredData.length;
        const escalatedCases = filteredData.filter(c => c.status === 'Escalated').length;
        return {
            alertsCount: totalAlertsReviewed,
            casesCount: totalCases,
            escalationsCount: escalatedCases,
            conversionRate: totalAlertsReviewed > 0 ? ((casesCreatedFromAlerts / totalAlertsReviewed) * 100).toFixed(1) : 0,
            escalationRate: totalCases > 0 ? ((escalatedCases / totalCases) * 100).toFixed(1) : 0,
        };
    }, [filteredData, allAlerts]);

    // NEW: Generate chart data inside a useEffect that depends on the filtered data
    useEffect(() => {
        if (filteredData.length > 0) {
            // Case Status Pie Chart
            const statusCounts = filteredData.reduce((acc, curr) => {
                acc[curr.status] = (acc[curr.status] || 0) + 1;
                return acc;
            }, {});
            setStatusPieData({
                labels: Object.keys(statusCounts),
                datasets: [{ data: Object.values(statusCounts), backgroundColor: ['#dc3545', '#ffc107', '#0dcaf0', '#198754', '#6c757d'] }]
            });

            // Risk Level Bar Chart
            const riskCounts = filteredData.reduce((acc, curr) => {
                acc[curr.risk] = (acc[curr.risk] || 0) + 1;
                return acc;
            }, {});
            setRiskBarData({
                labels: ['High', 'Medium', 'Low'],
                datasets: [{ label: '# of Cases', data: [riskCounts['High']||0, riskCounts['Medium']||0, riskCounts['Low']||0], backgroundColor: ['#e74a3b', '#f6c23e', '#858796'] }]
            });
            
            // Customer Type Bar Chart
            const typeCounts = filteredData.reduce((acc, curr) => {
                const type = curr.customerName.includes('Corp') || curr.customerName.includes('Traders') ? 'Corporate' : 'Individual';
                acc[type] = (acc[type] || 0) + 1;
                return acc;
            }, {});
            setCustomerTypeData({
                labels: Object.keys(typeCounts),
                datasets: [{ label: 'Cases by Customer Type', data: Object.values(typeCounts), backgroundColor: ['#4e73df', '#1cc88a'] }]
            });
        } else {
            // If there's no data after filtering, reset charts to the initial empty state
            setStatusPieData(initialChartData);
            setRiskBarData(initialChartData);
            setCustomerTypeData(initialChartData);
        }
    }, [filteredData]); // Re-run this effect whenever the filtered data changes
    
    // --- HANDLERS ---
    const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });
    
    const handleExportCSV = () => {
        if (filteredData.length === 0) { toast.error("No data to export."); return; }
        const headers = "CaseID,CustomerName,Status,Risk,AssignedTo,OpenDate\n";
        const rows = filteredData.map(c => `${c.id},"${c.customerName}",${c.status},${c.risk},${c.assignedTo},${c.openDate}`).join('\n');
        const csvContent = "data:text/csv;charset=utf-8," + headers + rows;
        const link = document.createElement("a");
        link.setAttribute("href", encodeURI(csvContent));
        link.setAttribute("download", `aml_report_${new Date().toISOString().split('T')[0]}.csv`);
        link.click();
        toast.success("CSV export started.");
    };

    const handleExportPDF = () => {
        const input = dashboardRef.current;
        html2canvas(input, { scale: 2 }).then(canvas => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`aml_dashboard_${new Date().toISOString().split('T')[0]}.pdf`);
        });
        toast.success("PDF export started.");
    };
    
    const handleExport = () => {
        if (reportFormat === 'CSV') {
            handleExportCSV();
        } else if (reportFormat === 'PDF') {
            handleExportPDF();
        } else {
            toast.info(`Simulating export of ${filteredData.length} records in ${reportFormat} format...`);
        }
    };
    const handleScheduleReport = () => {
        toast.info("Simulation: Report scheduled for daily distribution via email.");
    };

    if (isLoading) return <div className="text-center p-5"><Spinner animation="border" /></div>;

    // --- RENDER ---
    return (
        <PageContainer title="Interactive Reporting & Dashboards">
            <p>Generate ad-hoc reports by filtering data to analyze trends, KPIs, and staff performance.</p>
           <Card className="mb-4">
                <Card.Header as="h5">Ad-Hoc Report Filters</Card.Header>
                <Card.Body>
                    <Form as={Row} className="g-3">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Date Range (Start)</Form.Label>
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    isClearable
                                    placeholderText="Select start date"
                                    className="form-control" // Use Bootstrap's class
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                             <Form.Group>
                                <Form.Label>Date Range (End)</Form.Label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate}
                                    isClearable
                                    placeholderText="Select end date"
                                    className="form-control"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}><Form.Group><Form.Label>Case Status</Form.Label><Form.Select name="status" onChange={handleFilterChange}>
                            <option value="All">All Statuses</option>
                            {[...new Set(allCases.map(c => c.status))].map(s => <option key={s}>{s}</option>)}
                        </Form.Select></Form.Group></Col>
                        <Col md={2}><Form.Group><Form.Label>Risk Level</Form.Label><Form.Select name="risk" onChange={handleFilterChange}>
                            <option value="All">All Risks</option><option>High</option><option>Medium</option><option>Low</option>
                        </Form.Select></Form.Group></Col>
                        <Col md={2}><Form.Group><Form.Label>Assigned To</Form.Label><Form.Select name="assignedTo" onChange={handleFilterChange}>
                             <option value="All">All Users/Teams</option>
                            {[...new Set(allUsers.map(u => u.team))].map(t => <option key={t}>{t}</option>)}
                            <option disabled>──────────</option>
                            {allUsers.map(u => <option key={u.id}>{u.name}</option>)}
                        </Form.Select></Form.Group></Col>
                    </Form>
                </Card.Body>
            </Card>
            <div ref={dashboardRef}>
                {/* KEY METRICS */}
                <Row className="mb-4">
                    <Col><Card body className="text-center"><Card.Title>{kpis.casesCount}</Card.Title><Card.Text>Total Cases</Card.Text></Card></Col>
                    <Col><Card body className="text-center"><Card.Title>{kpis.alertsCount}</Card.Title><Card.Text>Alerts Reviewed</Card.Text></Card></Col>
                    <Col><Card body className="text-center"><Card.Title>{kpis.conversionRate}%</Card.Title><Card.Text>Alert-to-Case Conversion</Card.Text></Card></Col>
                    <Col><Card body className="text-center bg-warning text-dark"><Card.Title>{kpis.escalationsCount}</Card.Title><Card.Text>Cases Escalated</Card.Text></Card></Col>
                    <Col><Card body className="text-center"><Card.Title>{kpis.escalationRate}%</Card.Title><Card.Text>Case Escalation Rate</Card.Text></Card></Col>
                </Row>
                
                {/* CHARTS */}
                <Row>
                    <Col lg={4} className="mb-4"><Card><Card.Header>Cases by Status</Card.Header><Card.Body style={{height: '300px'}}><Pie data={statusPieData} options={{ responsive: true, maintainAspectRatio: false }}/></Card.Body></Card></Col>
                    <Col lg={4} className="mb-4"><Card><Card.Header>Cases by Risk</Card.Header><Card.Body style={{height: '300px'}}><Bar data={riskBarData} options={{ responsive: true, maintainAspectRatio: false }}/></Card.Body></Card></Col>
                    <Col lg={4} className="mb-4"><Card><Card.Header>Cases by Customer Type</Card.Header><Card.Body style={{height: '300px'}}><Bar data={customerTypeData} options={{ responsive: true, maintainAspectRatio: false }}/></Card.Body></Card></Col>
                </Row>
            </div>
            <Card>
                <Card.Header as="h5">Actions</Card.Header>
                <Card.Body className="d-flex justify-content-end gap-2">
                    <Button variant="success" onClick={handleExportCSV}><FiletypeCsv className="me-2" />Export Filtered Data to CSV</Button>
                    <Button variant="danger" onClick={handleExportPDF}><FiletypePdf className="me-2" />Export Dashboard as PDF</Button>
                    <Button variant="info" onClick={handleScheduleReport}>Schedule This Report</Button>
                </Card.Body>
                <Card.Body className="d-flex justify-content-end align-items-center gap-2">
                    <Form.Group as={Col} md="3">
                        <Form.Label className="visually-hidden">Report Format</Form.Label>
                        <Form.Select value={reportFormat} onChange={e => setReportFormat(e.target.value)}>
                            <option>CSV</option>
                            <option>PDF</option>
                            <option>XML (FATF)</option>
                            <option>XBRL (SAMA)</option>
                        </Form.Select>
                    </Form.Group>
                    <Button variant="success" onClick={handleExport}><FiletypeCsv className="me-2" />Export Report</Button>
                    <Button variant="info" onClick={handleScheduleReport}><CalendarEvent className="me-2" />Schedule</Button>
                </Card.Body>
            </Card>
        </PageContainer>
    );
};

export default ReportDashboard;